import '../i18n/autoTranslate';
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileText, Upload, X, AlertCircle, CheckCircle } from 'lucide-react';
import SpotlightCard from '../../components/ui/SpotlightCard';
import AppBackground from '../../components/ui/AppBackground.tsx';

/**
 * ResumeUploader.jsx
 * ------------------------------------------------------------
 * Context-aware version.
 *
 * Works in:
 * 1) Resume Builder
 *    <ResumeUploader source="resume-builder" onResumeExtracted={setResumeData} />
 *
 * 2) JobMatch AI
 *    <ResumeUploader source="job-match" redirectAfterUpload="/job-match-ai" />
 *
 * Also detects:
 * - ?from=job-match
 * - ?source=job-match
 * - location.state.from = "job-match"
 * - localStorage.resumeUploadSource = "job-match"
 *
 * ENV recommended:
 * VITE_RESUME_EXTRACTION_API_URL=https://your-node-backend.onrender.com
 * VITE_AUTH_API_BASE_URL=https://your-python-auth-backend.onrender.com
 */

const DEFAULT_EXTRACTION_API = 'https://ai-interview-back-node.onrender.com';

const getCleanBase = (value) => String(value || '').replace(/\/$/, '');

const RESUME_EXTRACTION_API_CANDIDATES = [
  import.meta.env.VITE_RESUME_EXTRACTION_API_URL,
  import.meta.env.VITE_EXTRACT_API_BASE_URL,
  import.meta.env.VITE_NODE_API_URL,
  import.meta.env.VITE_API_BASE_URL,
  DEFAULT_EXTRACTION_API,
]
  .filter(Boolean)
  .map(getCleanBase);

const AUTH_API_BASE_URL = getCleanBase(
  import.meta.env.VITE_AUTH_API_BASE_URL ||
    import.meta.env.VITE_PYTHON_API_URL ||
    import.meta.env.VITE_API_URL ||
    import.meta.env.VITE_API_BASE_URL ||
    DEFAULT_EXTRACTION_API
);

const JOB_MATCH_ROUTE = import.meta.env.VITE_JOB_MATCH_ROUTE || '/job-match-ai';
const RESUME_BUILDER_ROUTE = import.meta.env.VITE_RESUME_BUILDER_ROUTE || '/cv-builder';

const TOKEN_KEYS = ['token', 'accessToken', 'access_token', 'authToken', 'interviewgenie_token'];
const USER_KEYS = ['user', 'authUser', 'currentUser', 'interviewgenie_user', 'ig_user'];
const MAX_RESUME_SIZE_BYTES = 15 * 1024 * 1024;
const ACCEPTED_RESUME_EXTENSIONS = ['.pdf', '.docx'];

const getToken = () => {
  for (const key of TOKEN_KEYS) {
    const value = localStorage.getItem(key);
    if (value) return value;
  }
  return '';
};

const safeJsonParse = (value, fallback = null) => {
  try {
    if (!value) return fallback;
    if (typeof value === 'object') return value;
    return JSON.parse(value);
  } catch {
    return fallback;
  }
};

const updateStoredUserResumeData = (resumeData, apiUser = null) => {
  const updatedUsers = [];

  for (const key of USER_KEYS) {
    const storedUser = safeJsonParse(localStorage.getItem(key));

    if (storedUser && typeof storedUser === 'object') {
      const nextUser = {
        ...storedUser,
        ...(apiUser && typeof apiUser === 'object' ? apiUser : {}),
        resume_data: resumeData,
        resumeData,
      };

      localStorage.setItem(key, JSON.stringify(nextUser));
      updatedUsers.push(nextUser);
    }
  }

  // Important: create a local user even if cloud save fails or apiUser is null
  if (!updatedUsers.length) {
    const nextUser = {
      ...(apiUser && typeof apiUser === 'object' ? apiUser : {}),
      resume_data: resumeData,
      resumeData,
    };

    localStorage.setItem('user', JSON.stringify(nextUser));
    updatedUsers.push(nextUser);
  }

  localStorage.setItem('ig_resume_data', JSON.stringify(resumeData));

  return updatedUsers[0];
};

const saveResumeDataToUser = async (resumeData) => {
  const token = getToken();

  if (!token) {
    throw new Error('You need to be logged in to save resume data to your account.');
  }

  const endpoint = `${AUTH_API_BASE_URL}/api/auth/resume-data`;
  const payload = JSON.stringify({ resume_data: resumeData });

  // Try POST first because some proxies/dev servers are stricter with PUT.
  // Backend supports both POST and PUT.
  const methods = ['POST', 'PUT'];
  let lastError = null;

  for (const method of methods) {
    try {
      const response = await fetch(endpoint, {
        method,
        mode: 'cors',
        credentials: 'omit',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        lastError = new Error(data.message || data.error || `Failed to save resume data with ${method}.`);
        continue;
      }

      return data.user || data;
    } catch (error) {
      lastError = error;
    }
  }

  throw new Error(
    `${lastError?.message || 'Failed to fetch'} — Save endpoint: ${endpoint}. Check Flask CORS and VITE_AUTH_API_BASE_URL.`
  );
};

const getSourceFromLocation = (location, explicitSource = '') => {
  const params = new URLSearchParams(location?.search || '');

  const fromQuery = params.get('from') || params.get('source') || '';
  const fromState = location?.state?.from || location?.state?.source || '';
  const fromStorage = localStorage.getItem('resumeUploadSource') || '';
  const source = String(explicitSource || fromState || fromQuery || fromStorage || '').toLowerCase();
  const path = String(location?.pathname || '').toLowerCase();

  if (source.includes('job') || path.includes('job-match')) return 'job-match';

  if (
    source.includes('builder') ||
    source.includes('resume') ||
    source.includes('cv') ||
    path.includes('builder') ||
    path.includes('resume') ||
    path.includes('cv')
  ) {
    return 'resume-builder';
  }

  return 'resume-builder';
};

const getReturnRoute = (location, source) => {
  const params = new URLSearchParams(location?.search || '');

  const explicitReturnTo =
    location?.state?.returnTo ||
    params.get('returnTo') ||
    params.get('redirect') ||
    localStorage.getItem('resumeUploadReturnTo');

  if (explicitReturnTo) return explicitReturnTo;

  return source === 'job-match' ? JOB_MATCH_ROUTE : RESUME_BUILDER_ROUTE;
};

const createEmptyResumeData = () => ({
  objectif: '',
  rawText: '',
  personalInfo: {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    country: '',
  },
  workExperience: [],
  education: [],
  skills: [],
  skillsList: '',
  optional: {},
});

const formatExtractedResumeData = (extractedData = {}) => {
  const extractedPersonalInfo = extractedData?.personalInfo || {};
  const fullName = String(extractedPersonalInfo?.name || '').trim();
  const [firstNameFromFull, ...restName] = fullName ? fullName.split(/\s+/) : [];
  const lastNameFromFull = restName.join(' ').trim();

  const skills = Array.isArray(extractedData.skills)
    ? extractedData.skills
    : String(extractedData.skillsList || '')
        .split(/[,|•\n]/)
        .map((s) => s.trim())
        .filter(Boolean);

  return {
    objectif: extractedData.objectif || extractedData.summary || extractedData.optional?.summary || '',
    rawText: extractedData.rawText || extractedData.text || '',
    personalInfo: {
      firstName: extractedPersonalInfo?.firstName || firstNameFromFull || '',
      lastName: extractedPersonalInfo?.lastName || lastNameFromFull || '',
      email: extractedPersonalInfo?.email || '',
      phone: extractedPersonalInfo?.phone || '',
      address: extractedPersonalInfo?.address || '',
      country: extractedPersonalInfo?.country || '',
    },
    workExperience:
      extractedData.workExperience?.map((exp) => ({
        id: Date.now() + Math.random(),
        title: exp.title || '',
        company: exp.company || '',
        description: exp.description || '',
        startDate: exp.startDate || '',
        endDate: exp.endDate || '',
        period: exp.period || '',
      })) || [],
    education:
      extractedData.education?.map((edu) => ({
        id: Date.now() + Math.random(),
        degree: edu.degree || '',
        institution: edu.institution || '',
        startDate: edu.startDate || '',
        endDate: edu.endDate || '',
        period: edu.period || '',
      })) || [],
    skills,
    skillsList: skills.join(', '),
    optional: {
      ...(extractedData.optional || {}),
      summary: extractedData.objectif || extractedData.optional?.summary || '',
    },
  };
};

const hasMeaningfulResumeData = (data) => {
  if (!data || typeof data !== 'object') return false;

  const personal = data.personalInfo || {};

  return Boolean(
    String(data.objectif || data.summary || data.rawText || '').trim() ||
      String(personal.firstName || '').trim() ||
      String(personal.lastName || '').trim() ||
      String(personal.email || '').trim() ||
      String(personal.phone || '').trim() ||
      String(personal.address || '').trim() ||
      String(personal.country || '').trim() ||
      (Array.isArray(data.skills) && data.skills.length > 0) ||
      (Array.isArray(data.workExperience) && data.workExperience.length > 0) ||
      (Array.isArray(data.education) && data.education.length > 0)
  );
};

const normalizeResumeCandidate = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const parsed = safeJsonParse(value);
    return parsed && typeof parsed === 'object' ? parsed : null;
  }

  return typeof value === 'object' ? value : null;
};

const getExtractedDataFromResponse = (payload = {}) => {
  const candidates = [
    payload.extractedData,
    payload.resumeData,
    payload.resume_data,
    payload.parsedData,
    payload.data?.extractedData,
    payload.data?.resumeData,
    payload.data?.resume_data,
    payload.data,
    payload.result?.extractedData,
    payload.result?.resumeData,
    payload.result,
    payload.rawText ? { rawText: payload.rawText, text: payload.rawText } : null,
    payload.text ? { rawText: payload.text, text: payload.text } : null,
  ];

  return (
    candidates
      .map(normalizeResumeCandidate)
      .find((item) => hasMeaningfulResumeData(item)) || null
  );
};

const extractResumeWithFallback = async (file) => {
  const endpointPaths = ['/extract-text', '/api/extract-text', '/api/resume/extract-text'];
  const attempts = [];
  let lastError = null;

  for (const base of RESUME_EXTRACTION_API_CANDIDATES) {
    for (const path of endpointPaths) {
      const endpoint = `${base}${path}`;
      const formData = new FormData();
      formData.append('resume', file);

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          body: formData,
        });

        const contentType = response.headers.get('content-type') || '';
        const result = contentType.includes('application/json')
          ? await response.json().catch(() => ({}))
          : {};

        const extractedData = getExtractedDataFromResponse(result);

        attempts.push({
          endpoint,
          status: response.status,
          ok: response.ok,
          success: result?.success,
          hasExtractedData: Boolean(extractedData),
          keys: Object.keys(result || {}),
        });

        if (response.ok && result?.success !== false && extractedData) {
          return {
            endpoint,
            result,
            extractedData: {
              ...extractedData,
              rawText: extractedData.rawText || result.rawText || '',
            },
            attempts,
          };
        }

        lastError = new Error(
          result?.message ||
            result?.error ||
            `Extraction response was successful but no extractedData was found at ${endpoint}`
        );
      } catch (error) {
        attempts.push({
          endpoint,
          status: 'network-error',
          ok: false,
          success: false,
          hasExtractedData: false,
        });

        lastError = error;
      }
    }
  }

  const error = new Error(
    lastError?.message ||
      'Failed to connect to extraction service. Set VITE_RESUME_EXTRACTION_API_URL correctly.'
  );

  error.attempts = attempts;
  throw error;
};

const validateResumeFile = (selectedFile) => {
  if (!selectedFile) return { ok: false, message: 'Please select a resume file.' };

  const fileName = String(selectedFile.name || '').toLowerCase();
  const isAccepted = ACCEPTED_RESUME_EXTENSIONS.some((ext) => fileName.endsWith(ext));

  if (!isAccepted) {
    return { ok: false, message: 'Unsupported file type. Please upload a PDF or DOCX resume.' };
  }

  if (selectedFile.size > MAX_RESUME_SIZE_BYTES) {
    return { ok: false, message: 'Resume is too large. Please upload a file smaller than 15MB.' };
  }

  return { ok: true, message: '' };
};

const getFileIcon = (fileName) => {
  if (fileName?.toLowerCase()?.endsWith('.pdf')) {
    return <FileText className="h-5 w-5 text-red-400" />;
  }

  if (fileName?.toLowerCase()?.endsWith('.docx')) {
    return <FileText className="h-5 w-5 text-blue-400" />;
  }

  return <FileText className="h-5 w-5 text-gray-400" />;
};

const getFileType = (fileName) => {
  if (fileName?.toLowerCase()?.endsWith('.pdf')) return 'PDF';
  if (fileName?.toLowerCase()?.endsWith('.docx')) return 'DOCX';
  return 'Document';
};

const ResumeUploader = ({
  onResumeExtracted,
  className = '',
  source,
  saveToProfile = true,
  redirectAfterUpload,
  autoNavigateBack = true,
  showFullHeader = false,
}) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const uploadSource = useMemo(() => getSourceFromLocation(location, source), [location, source]);
  const returnRoute = useMemo(() => getReturnRoute(location, uploadSource), [location, uploadSource]);

  const { user } = useSelector((state) => ({
    user:
      state?.auth?.user ||
      state?.user?.user ||
      state?.user ||
      state?.auth?.currentUser ||
      null,
  }));

  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [debugAttempts, setDebugAttempts] = useState([]);
  const [alertInfo, setAlertInfo] = useState({
    show: false,
    variant: 'default',
    title: '',
    description: '',
  });

  const isJobMatchFlow = uploadSource === 'job-match';

  const showAlert = (variant, title, description) => {
    setAlertInfo({
      show: true,
      variant,
      title,
      description,
    });

    setTimeout(() => {
      setAlertInfo((prev) => ({ ...prev, show: false }));
    }, 6000);
  };

  const syncResumeDataInClient = (resumeData, apiUser = null) => {
    const updatedUser = updateStoredUserResumeData(resumeData, apiUser || user);

    // Supports common Redux reducers without forcing one exact slice name.
    dispatch({ type: 'auth/updateUser', payload: updatedUser });
    dispatch({ type: 'auth/setUser', payload: updatedUser });
    dispatch({ type: 'user/updateUser', payload: updatedUser });
    dispatch({ type: 'user/setUser', payload: updatedUser });

    return updatedUser;
  };

  const persistResumeData = async (resumeData) => {
    const apiUser = await saveResumeDataToUser(resumeData);
    syncResumeDataInClient(resumeData, apiUser);
    return apiUser;
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    const validation = validateResumeFile(selectedFile);
    if (!validation.ok) {
      event.target.value = '';
      setFile(null);
      showAlert('destructive', 'Invalid file', validation.message);
      return;
    }

    setFile(selectedFile);
    setDebugAttempts([]);
  };

  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.type === 'dragenter' || event.type === 'dragover') {
      setDragActive(true);
    } else if (event.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setDragActive(false);

    const droppedFile = event.dataTransfer.files?.[0];
    if (!droppedFile) return;

    const validation = validateResumeFile(droppedFile);
    if (!validation.ok) {
      setFile(null);
      showAlert('destructive', 'Invalid file', validation.message);
      return;
    }

    setFile(droppedFile);
    setDebugAttempts([]);
  };

  const removeFile = () => {
    setFile(null);
  };

  const finishSuccessFlow = () => {
    if (redirectAfterUpload || (autoNavigateBack && isJobMatchFlow)) {
      setTimeout(() => {
        navigate(redirectAfterUpload || returnRoute);
      }, 700);
    }
  };

  const handleUpload = async () => {
    if (!file) return;

    const validation = validateResumeFile(file);
    if (!validation.ok) {
      showAlert('destructive', 'Invalid file', validation.message);
      return;
    }

    setLoading(true);
    setDebugAttempts([]);

    showAlert(
      'default',
      'Processing Resume',
      isJobMatchFlow
        ? 'Extracting your resume and preparing JobMatch AI...'
        : 'Extracting information from your resume...'
    );

    try {
      const { result, endpoint, extractedData, attempts } = await extractResumeWithFallback(file);
      setDebugAttempts(attempts);
      
      const formattedData = formatExtractedResumeData({
        ...extractedData,
        rawText: extractedData.rawText || result.rawText || '',
      });

      if (onResumeExtracted) {
        onResumeExtracted(formattedData, {
          source: uploadSource,
          endpoint,
          returnRoute,
        });
      }

      syncResumeDataInClient(formattedData);

      if (saveToProfile) {
        try {
          const apiUser = await persistResumeData(formattedData);
          syncResumeDataInClient(formattedData, apiUser);
        } catch (saveError) {
          console.error('Resume extracted, but online save failed:', saveError);
      
          showAlert(
            'default',
            'Resume extracted locally',
            `Cloud save failed, but JobMatch AI can use the resume now. Details: ${saveError.message}`
          );
      
          finishSuccessFlow();
          return;
        }
      } else {
        syncResumeDataInClient(formattedData);
      }

      showAlert(
        'default',
        'Success!',
        isJobMatchFlow
          ? 'Resume extracted and saved. Returning to JobMatch AI...'
          : 'Resume extracted and saved to your profile successfully.'
      );

      finishSuccessFlow();
    } catch (error) {
      console.error('Error uploading resume:', error);
      console.table?.(error?.attempts || []);
      setDebugAttempts(error?.attempts || []);

      const emptyData = createEmptyResumeData();

      if (onResumeExtracted) {
        onResumeExtracted(emptyData, {
          source: uploadSource,
          error,
          returnRoute,
        });
      }

      showAlert(
        'destructive',
        'Extraction failed',
        `${error?.message || 'Could not extract this resume.'} Current flow: ${uploadSource}.`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AppBackground className={className}>


      <div className={`${showFullHeader ? 'mt-16 pt-10' : 'pt-0'} pb-32 px-4 md:px-6 lg:px-8 space-y-4`}>
        {alertInfo.show && (
          <div
            className={`p-4 rounded-lg border ${
              alertInfo.variant === 'destructive'
                ? 'bg-red-600/10 border-red-500/30 text-red-300'
                : 'bg-blue-600/10 border-blue-500/30 text-blue-300'
            }`}
          >
            <div className="flex items-center space-x-2">
              {alertInfo.variant === 'destructive' ? (
                <AlertCircle className="h-4 w-4 shrink-0" />
              ) : (
                <CheckCircle className="h-4 w-4 shrink-0" />
              )}

              <div>
                <p className="font-medium">{alertInfo.title}</p>
                <p className="text-sm opacity-90">{alertInfo.description}</p>
              </div>
            </div>
          </div>
        )}

        <SpotlightCard spotlightColor="rgba(99, 102, 241, 0.15)">
          <div className="bg-gradient-to-br from-[rgba(30,30,46,0.8)] via-[rgba(30,30,46,0.7)] to-[rgba(46,46,69,0.4)] border border-white/10 p-6">
            <div
              className={`relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 ${
                dragActive ? 'border-indigo-500 bg-indigo-500/10' : 'border-gray-600 hover:border-gray-500'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />

              <div className="space-y-2">
                <p className="text-lg font-medium text-white">
                  Drop your resume here, or{' '}
                  <label htmlFor="resume-upload" className="text-indigo-400 cursor-pointer hover:text-indigo-300">
                    browse
                  </label>
                </p>
                <p className="text-sm text-gray-400">Supports PDF and DOCX files. Max recommended size: 10MB.</p>
              </div>

              <input
                id="resume-upload"
                type="file"
                accept=".pdf,.docx"
                onChange={handleFileChange}
                className="hidden"
              />
            </div>
          </div>
        </SpotlightCard>

        {file && (
          <SpotlightCard spotlightColor="rgba(34, 197, 94, 0.15)">
            <div className="bg-gradient-to-br from-[rgba(30,30,46,0.8)] via-[rgba(30,30,46,0.7)] to-[rgba(46,46,69,0.4)] border border-white/10 p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3 min-w-0">
                  {getFileIcon(file.name)}

                  <div className="min-w-0">
                    <span className="text-white font-medium block truncate">{file.name}</span>
                    <span className="text-gray-400 text-sm">
                      {getFileType(file.name)} • {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                </div>

                <button onClick={removeFile} className="text-gray-400 hover:text-white transition-colors p-1 rounded">
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>
          </SpotlightCard>
        )}

        <button
          onClick={handleUpload}
          disabled={!file || loading}
          className="w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg font-medium hover:from-indigo-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center space-x-2"
        >
          {loading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              <span>Processing Resume...</span>
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" />
              <span>{isJobMatchFlow ? 'Upload, Save & Continue' : 'Upload and Extract Data'}</span>
            </>
          )}
        </button>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-400">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-400" />
            <span>AI-powered extraction</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-400" />
            <span>Saves to user.resume_data</span>
          </div>
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-4 w-4 text-green-400" />
            <span>JobMatch-aware return flow</span>
          </div>
        </div>

        <div className="bg-gray-800/50 border border-gray-700 rounded-lg p-4">
          <p className="text-xs text-gray-400 text-center">
            <strong className="text-gray-300">Privacy Focused:</strong> Your resume is processed securely. Extracted
            resume data is saved only to your own profile so features like JobMatch AI can use it.
          </p>
        </div>

        {debugAttempts.length > 0 && (
          <details className="bg-black/30 border border-white/10 rounded-lg p-4 text-xs text-gray-400">
            <summary className="cursor-pointer text-gray-300">Extraction debug attempts</summary>
            <pre className="mt-3 overflow-x-auto whitespace-pre-wrap">
              {JSON.stringify(debugAttempts, null, 2)}
            </pre>
          </details>
        )}
      </div>
    </AppBackground>
  );
};

export default ResumeUploader;
